if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      createCertificateLinkToken(formData) {
        return [
          formData.get('id') || '',
          Date.now(),
          Math.random().toString(36).slice(2, 10),
        ].join('-');
      }

      getSelectedVariantSummary() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return '';

        const summaryParts = [];
        const fieldsets = productInfo.querySelectorAll('variant-selects fieldset.product-form__input');
        const dropdowns = productInfo.querySelectorAll('variant-selects .product-form__input--dropdown');

        fieldsets.forEach((fieldset) => {
          const legend = fieldset.querySelector('legend');
          const selectedValue = fieldset.querySelector('[data-selected-value]');
          if (!legend || !selectedValue) return;

          const optionName = legend.childNodes[0]?.textContent?.replace(':', '').trim();
          const optionValue = selectedValue.textContent.trim();
          if (optionName && optionValue) {
            summaryParts.push(`${optionName}: ${optionValue}`);
          }
        });

        dropdowns.forEach((dropdown) => {
          const label = dropdown.querySelector('label');
          const select = dropdown.querySelector('select');
          if (!label || !select) return;

          const optionName = label.textContent.trim();
          const optionValue = select.value.trim();
          if (optionName && optionValue) {
            summaryParts.push(`${optionName}: ${optionValue}`);
          }
        });

        return summaryParts.join(', ');
      }

      addCertificateAddon(addonWrapper, formData) {
        const addonVariantId = parseInt(addonWrapper.dataset.addonVariantId, 10);
        const quantity = parseInt(formData.get('quantity') || 1, 10);
        const variantSummary = this.getSelectedVariantSummary();
        const certificateFor = variantSummary
          ? `${this.dataset.productTitle || document.title} (${variantSummary})`
          : this.dataset.productTitle || document.title;
        const certificateLinkToken = formData.get('properties[_certificate_link_token]');

        if (!addonVariantId) {
          return Promise.resolve(null);
        }

        const payload = {
          items: [
            {
              id: addonVariantId,
              quantity,
              properties: {
                'Certificate For': certificateFor,
                '_certificate_addon': 'true',
                '_certificate_link_token': certificateLinkToken,
                '_certificate_parent_variant_id': String(formData.get('id')),
              },
            },
          ],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            ...fetchConfig('javascript').headers,
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        }).then((response) => response.json());
      }

      handleSuccessfulAdd(response, formData, evt) {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }

        CartPerformance.measureFromEvent('add:user-action', evt);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const addonWrapper = this.form.querySelector('.certificate-addon-wrapper');
        const addonCheckbox = this.form.querySelector('[data-addon-checkbox]');
        const addonSelected =
          Boolean(addonWrapper && addonCheckbox && addonCheckbox.checked && addonWrapper.dataset.addonVariantId);

        if (addonSelected) {
          formData.append('properties[_certificate_link_token]', this.createCertificateLinkToken(formData));
        }

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          this.cart.setActiveElement(document.activeElement);
        }
        if (this.cart) {
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.handleSuccessfulAdd(response, formData, evt);

            if (addonSelected) {
              this.addCertificateAddon(addonWrapper, formData).then((addonResponse) => {
                if (addonResponse && addonResponse.status) {
                  console.error('Certificate addon error:', addonResponse.description || addonResponse.errors);
                  this.handleErrorMessage('Certificate could not be added. The main product was added to cart.');
                  return;
                }

                if (!addonResponse || !this.cart) return;

                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form-addon',
                  productVariantId: formData.get('id'),
                  cartData: addonResponse,
                });

                if (typeof this.cart.renderContents === 'function') {
                  this.cart.renderContents(addonResponse);
                }
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
